import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import Decimal from 'decimal.js';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ItemDiscountConfig } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { usePluralization } from 'common/hooks';
import { typographyCss } from 'common/typography';
import { convertDecimalToMonetaryInt } from 'common/utils/currencyDecimals';
import { DiscountedItem } from 'modules/campaigns/hooks/useDiscountedItems';
import { useIsMultiItemCampaignSupported } from 'modules/campaigns/hooks/useIsMultiItemCampaignSupported';
import { useMenuItems } from 'modules/campaigns/hooks/useMenuItems';

import { DiscountedItemsGrid } from './DiscountedItemsGrid';
import CampaignFormItem, { CampaignFormView } from '../CampaignFormItem';
import { DiscountedItemDetails } from '../discounted-item-section/DiscountedItemDetails/DiscountedItemDetails';

type DiscountedItemsCountCalculationProps = {
  isEndOfDayCampaign: true;
  totalBudget: number | null;
  itemDiscount: ItemDiscountConfig;
  currency: Currency;
};

type DiscountedItemsDetailsProps = {
  selectedItemIds: string[];
  menuId: string;
} & ({ itemTriggerCount: number; isEndOfDayCampaign?: false } | DiscountedItemsCountCalculationProps);

const Divider = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const ValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-block-start: 4px;
`;

const ItemCountText = styled.p`
  ${typographyCss.Body3}
  margin: 0;
`;

const calculateDiscountedItemsCount = (
  { totalBudget, itemDiscount, currency }: DiscountedItemsCountCalculationProps,
  selectedItemIds: string[],
  menuItems: DiscountedItem[],
) => {
  if (!totalBudget) {
    return 0;
  }

  if (itemDiscount.amount) {
    return Decimal.div(totalBudget, itemDiscount.amount).round().toNumber();
  }

  const highestItemPrice = menuItems.reduce((highestPrice, item) => {
    if (selectedItemIds.includes(item.id)) {
      const price = parseFloat(item.baseprice.amount);
      return price > highestPrice ? price : highestPrice;
    }
    return highestPrice;
  }, 0);
  const highesItemPriceMonetary = convertDecimalToMonetaryInt(highestItemPrice, currency);

  if (itemDiscount.percentage && highesItemPriceMonetary) {
    return Decimal.div(totalBudget, Decimal.div(itemDiscount.percentage, 100).mul(highesItemPriceMonetary))
      .round()
      .toNumber();
  }

  return 0;
};

export const DiscountedItemsDetails = ({ selectedItemIds, menuId, ...rest }: DiscountedItemsDetailsProps) => {
  const { menuItems, isLoadingMenuData } = useMenuItems(menuId);
  const { FormattedPluralMessage } = usePluralization();
  const { isMultiItemCampaignSupported } = useIsMultiItemCampaignSupported();

  // Display all items even if multi-item campaign creation is not supported if the campaign already has multiple items
  const showOldSingleItemContent =
    (!!rest.isEndOfDayCampaign || !isMultiItemCampaignSupported) && selectedItemIds.length <= 1;

  return (
    <CampaignFormItem
      title={
        showOldSingleItemContent ? (
          <FormattedMessage id="campaigns.form.item.what-item-has" />
        ) : (
          <FormattedMessage id="campaigns.form.item.which-items-have-discount" />
        )
      }
      value={
        <ValueContainer>
          {showOldSingleItemContent ? (
            <DiscountedItemDetails itemId={selectedItemIds[0]} menuId={menuId} />
          ) : (
            <DiscountedItemsGrid
              selectedItemIds={selectedItemIds}
              menuItems={menuItems}
              isLoadingMenuData={isLoadingMenuData}
            />
          )}
          <Divider />
          {!rest.isEndOfDayCampaign ? (
            <CampaignFormView
              title={<FormattedMessage id="campaigns.form.item-count.how-many" />}
              value={
                <ItemCountText>
                  <FormattedPluralMessage
                    id="campaigns.form.item-count.customer-needs-buy"
                    count={rest.itemTriggerCount}
                  />
                </ItemCountText>
              }
            />
          ) : (
            <CampaignFormView
              title={<FormattedMessage id="campaigns.form.end-of-day-items-count.title" />}
              value={
                isLoadingMenuData ? (
                  <Spinner size="tiny" />
                ) : (
                  <ItemCountText>
                    <FormattedPluralMessage
                      id="campaigns.form.end-of-day-items-count.view-description"
                      count={calculateDiscountedItemsCount(rest, selectedItemIds, menuItems)}
                    />
                  </ItemCountText>
                )
              }
            />
          )}
        </ValueContainer>
      }
    />
  );
};
